// Phase 44.31 — SessionsCard riusabile (profilo cliente + driver).
//
// Mostra le sessioni attive dell'utente con:
//   - device label (iPhone Safari, Mac Chrome, …)
//   - IP + last_seen
//   - sessione corrente evidenziata (badge "Questo dispositivo")
//   - bottone "Esci da questo dispositivo" per ogni sessione non-corrente
//   - bottone "Esci da tutti gli altri dispositivi"

import { useEffect, useState } from "react";
import { Smartphone, Monitor, Tablet, Globe, ShieldCheck, Loader2, LogOut, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ICONS = {
  iphone: Smartphone, ipad: Tablet, android: Smartphone,
  mac: Monitor, windows: Monitor, linux: Monitor, unknown: Globe,
};

function timeAgo(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
    if (s < 60) return "ora";
    if (s < 3600) return `${Math.floor(s / 60)} min fa`;
    if (s < 86400) return `${Math.floor(s / 3600)} h fa`;
    if (s < 30 * 86400) return `${Math.floor(s / 86400)} giorni fa`;
    return new Intl.DateTimeFormat("it-CH", { dateStyle: "short" }).format(d);
  } catch {
    return iso;
  }
}

export default function SessionsCard({ testid = "sessions-card" }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmGlobal, setConfirmGlobal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/sessions");
      setSessions(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento sessioni");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const revokeOne = async (sid) => {
    setBusyId(sid);
    try {
      await api.delete(`/auth/sessions/${sid}`);
      toast.success("Sessione disconnessa");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore disconnessione");
    } finally {
      setBusyId(null);
    }
  };

  const revokeOthers = async () => {
    setBusyId("__others__");
    try {
      const { data } = await api.post("/auth/sessions/revoke-all-others");
      const n = data?.revoked || 0;
      toast.success(n > 0 ? `Disconnesse ${n} sessioni` : "Nessun'altra sessione attiva");
      setConfirmGlobal(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore logout globale");
    } finally {
      setBusyId(null);
    }
  };

  const others = sessions.filter((s) => !s.is_current);

  return (
    <div className="card-soft p-5 sm:p-6 space-y-4" data-testid={testid}>
      <div className="flex items-start gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-dapnow-blue-soft text-dapnow-blue shrink-0">
          <ShieldCheck className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="overline">Sicurezza</p>
          <p className="font-display text-lg font-bold tracking-tight mt-0.5">Sessioni attive</p>
          <p className="text-xs text-dapnow-muted mt-1 leading-relaxed">
            Gestisci i dispositivi collegati al tuo account DapNow.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-dapnow-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Caricamento sessioni…
        </div>
      ) : sessions.length === 0 ? (
        <p className="text-sm text-dapnow-muted">Nessuna sessione attiva.</p>
      ) : (
        <ul className="space-y-2" data-testid={`${testid}-list`}>
          {sessions.map((s) => {
            const Icon = ICONS[s.device_kind] || Globe;
            return (
              <li
                key={s.id}
                className={`rounded-xl border px-4 py-3 ${s.is_current ? "border-dapnow-blue bg-dapnow-blue-soft/30" : "border-dapnow-line bg-white"}`}
                data-testid={`${testid}-item-${s.id}`}
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-dapnow-bg text-dapnow-ink shrink-0">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold tracking-tight text-sm truncate">{s.device_label}</p>
                      {s.is_current && (
                        <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider bg-dapnow-blue text-white px-2 py-0.5 rounded-full" data-testid={`${testid}-current-badge`}>
                          Questo dispositivo
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-dapnow-muted truncate">
                      {s.ip || "IP sconosciuto"} · ultimo accesso {timeAgo(s.last_seen_at)}
                    </p>
                  </div>
                  {!s.is_current && (
                    <button
                      type="button"
                      onClick={() => revokeOne(s.id)}
                      disabled={busyId === s.id}
                      data-testid={`${testid}-revoke-${s.id}`}
                      className="inline-flex items-center gap-1 h-8 px-3 rounded-lg text-xs font-semibold border border-dapnow-line text-dapnow-ink hover:bg-dapnow-bg disabled:opacity-50 shrink-0"
                    >
                      {busyId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <LogOut className="h-3 w-3" />}
                      Esci
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {others.length > 0 && !confirmGlobal && (
        <button
          type="button"
          onClick={() => setConfirmGlobal(true)}
          data-testid={`${testid}-revoke-others-btn`}
          className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl border border-dapnow-line bg-white text-sm font-semibold hover:bg-dapnow-bg transition"
        >
          <LogOut className="h-4 w-4" />
          Esci da tutti gli altri dispositivi
        </button>
      )}
      {confirmGlobal && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-3" data-testid={`${testid}-confirm-global`}>
          <p className="text-sm text-amber-900 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
            Disconnetterò {others.length} {others.length === 1 ? "sessione" : "sessioni"}. Questo dispositivo resterà attivo.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={revokeOthers}
              disabled={busyId === "__others__"}
              data-testid={`${testid}-revoke-others-confirm`}
              className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-amber-600 text-white text-sm font-bold hover:bg-amber-500 disabled:opacity-50"
            >
              {busyId === "__others__" ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
              Conferma logout globale
            </button>
            <button
              type="button"
              onClick={() => setConfirmGlobal(false)}
              className="px-4 rounded-lg border border-dapnow-line bg-white text-sm font-semibold hover:bg-white/80"
            >
              Annulla
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
